import { useState, type FormEvent } from 'react';
import { Link } from '../../../components/Link';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '../../../lib/api';
import { Icon } from '../../../components/icons';
import { Button, Card, Field, Input, cn } from '../../../components/ui';
import { DeferredTurnstileWidget } from '../../../components/TurnstileWidget';
import { useTurnstileGate } from '../../../lib/useAbuseConfig';
import { ToolPageLayout, type ToolPageContent } from './ToolPageLayout';

interface BlacklistEntry {
  zone: string;
  name: string;
  description: string;
  status: 'listed' | 'clean' | 'error';
  tier: 'reputation' | 'network';
  responseIp?: string;
  error?: string;
}

interface BlacklistResult {
  query: string;
  resolvedIp: string;
  network: string | null;
  checkedAt: string;
  totalChecked: number;
  totalListed: number;
  reputationListed: number;
  entries: BlacklistEntry[];
}

const STATUS_STYLE: Record<BlacklistEntry['status'], { text: string; bg: string; label: string }> = {
  listed: { text: 'text-review', bg: 'bg-review/15', label: 'Listed' },
  clean: { text: 'text-allow', bg: 'bg-allow/15', label: 'Clean' },
  error: { text: 'text-faint', bg: 'bg-panel2', label: 'No answer' },
};

/**
 * Free DNSBL blacklist checker — hits /public/tools/blacklist. Pure DNS on the
 * backend (zero provider credits), but RBL probes are I/O-bound so the work
 * runs server-side rather than from the browser.
 */
export function BlacklistCheckerPage() {
  const [target, setTarget] = useState('');
  const [rateLimited, setRateLimited] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const gate = useTurnstileGate();

  const check = useMutation<BlacklistResult, ApiError, string>({
    mutationFn: (t) =>
      api<BlacklistResult>('/public/tools/blacklist', {
        method: 'POST',
        body: { target: t },
        auth: false,
        headers: gate.headers,
      }),
    onSuccess: () => {
      setRateLimited(false);
      setError(null);
    },
    onError: (e) => {
      if (e.status === 429) {
        setRateLimited(true);
        setError(null);
      } else {
        setError(e instanceof Error ? e.message : 'Lookup failed.');
        setRateLimited(false);
      }
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!target.trim()) return;
    check.mutate(target.trim());
  }

  const content: ToolPageContent = {
    slug: 'blacklist-checker',
    title: 'Email blacklist checker',
    tagline: 'Check any IP or domain against 12+ public DNSBLs in seconds.',
    eyebrow: 'Free tool · No signup · DNS-only',
    description:
      'Free email blacklist checker. Query 12+ public RBLs (Spamhaus, Barracuda, SpamCop, SORBS, …) in parallel for any IP or domain — no signup, no API key.',
    icon: <Icon.Crosshair />,
    accent: 'review',
    intro:
      'Paste an IPv4 address or sending domain — we check it against Spamhaus, Barracuda, SpamCop, the SORBS family, UCEPROTECT, Mailspike, CBL, GBUdb, and PSBL in parallel. Listings tell you why mailbox providers are downranking your mail.',
    body: (
      <Card className="p-6">
        <form onSubmit={onSubmit} {...gate.armProps} className="grid gap-4 sm:grid-cols-[1fr_auto]">
          <Field label="IPv4 address or domain">
            <Input
              value={target}
              onChange={(e) => setTarget(e.target.value.toLowerCase().trim())}
              placeholder="198.51.100.42  or  example.com"
              maxLength={255}
              autoFocus
            />
          </Field>
          <Button
            type="submit"
            variant="primary"
            disabled={check.isPending || !target.trim() || !gate.ready}
            className="h-11 self-end px-5"
          >
            {check.isPending ? 'Checking 12 lists…' : 'Check blacklists'}
          </Button>
        </form>
        {gate.siteKey && (
          <div className="mt-3">
            <DeferredTurnstileWidget gate={gate} />
          </div>
        )}
        {error && <p className="mt-3 text-[13px] text-review">{error}</p>}
        {rateLimited && (
          <p className="mt-3 text-[13px] text-verify">
            You've hit the free daily limit (8 / IP). <Link to="/register" className="text-accent hover:underline">Create a workspace</Link> for unlimited checks + monitoring.
          </p>
        )}

        {check.data && (
          <div className="mt-6">
            <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-bordersoft pb-4">
              <div>
                <div className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-faint">{check.data.query}</div>
                <div className="mt-1 text-base font-semibold">
                  {check.data.reputationListed === 0 ? (
                    <span className="text-allow">Clean — no reputation-list hits across {check.data.totalChecked} lists.</span>
                  ) : (
                    <span className="text-review">
                      Listed on {check.data.reputationListed} reputation list{check.data.reputationListed === 1 ? '' : 's'}.
                    </span>
                  )}
                </div>
                {check.data.resolvedIp !== check.data.query && (
                  <div className="mt-1 font-mono text-[11.5px] text-muted">Resolved to {check.data.resolvedIp}</div>
                )}
                {check.data.network && (
                  <p className="mt-2 max-w-md rounded-md border border-verify/30 bg-verify/10 px-3 py-2 text-[12px] leading-relaxed text-verify">
                    This domain is behind <strong>{check.data.network}</strong>, so it resolves to {check.data.network}'s
                    shared edge IP. These results reflect that CDN's infrastructure — <strong>not</strong> your domain or
                    the IP you actually send email from. To check sending reputation, run this on your mail server / ESP IP.
                  </p>
                )}
              </div>
              <div className="font-mono text-[10.5px] text-faint">
                Checked at {new Date(check.data.checkedAt).toLocaleTimeString()}
              </div>
            </div>

            <ul className="mt-4 divide-y divide-bordersoft">
              {check.data.entries.map((entry) => (
                <li key={entry.zone} className="flex flex-wrap items-baseline justify-between gap-3 py-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-[13.5px] font-medium">{entry.name}</span>
                      {entry.tier === 'network' && (
                        <span className="rounded bg-panel2 px-1.5 py-0.5 font-mono text-[9.5px] uppercase tracking-wider text-faint">
                          network-level · informational
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 font-mono text-[11px] text-faint">{entry.zone}</div>
                    <p className="mt-1 max-w-md text-[12.5px] text-muted">{entry.description}</p>
                    {entry.error && (
                      <p className="mt-1 font-mono text-[11px] text-verify">{entry.error}</p>
                    )}
                  </div>
                  <span
                    className={cn(
                      'rounded-md px-2.5 py-1 font-mono text-[11px] uppercase tracking-wider',
                      STATUS_STYLE[entry.status].text,
                      STATUS_STYLE[entry.status].bg,
                    )}
                  >
                    {STATUS_STYLE[entry.status].label}
                    {entry.responseIp && <span className="ml-2 font-mono text-[10px] opacity-70">{entry.responseIp}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card>
    ),
    educational: {
      title: 'How to read this result',
      paragraphs: [
        'The lists are not equal, and this is the part that causes unnecessary panic. A Spamhaus ZEN listing is the one that actually stops mail; a hit on an obscure regional list is usually informational.',
        'A clean check now does not guarantee a clean check tomorrow — listings arrive silently and you normally find out from a customer. The Blacklist Monitoring add-on re-checks 90+ lists daily on every IP and domain you onboard.',
      ],
    },
    faq: [
      { q: 'Which lists does this check?', a: 'Spamhaus ZEN, Barracuda BRBL, SpamCop SCBL, SORBS (dnsbl + spam), CBL/Abuseat, PSBL, UCEPROTECT (L1/L2/L3), Mailspike, GBUdb Truncate. Twelve lists in total, covering ~95% of what mailbox providers actually use.' },
      { q: 'I\'m on UCEPROTECT L2 but nothing else. What does that mean?', a: 'L2 lists /24 networks — your specific IP isn\'t individually flagged, but a neighbour on the same /24 is. UCEPROTECT is the only major list that does network-level listings, and most mailbox providers don\'t consult L2 / L3 directly. Less urgent than a SBL hit.' },
      { q: 'How do I get delisted?', a: 'Each list publishes its own delisting process. Spamhaus has a self-service portal for ZEN; Barracuda has a delisting form; SpamCop expects the issue (compromise / list hygiene) to be resolved before the listing auto-expires. Links are on each list\'s site.' },
      { q: 'I\'m listed but my mail still gets delivered?', a: 'Possible — not every mailbox provider checks every list. Major providers check Spamhaus heavily and SpamCop / Barracuda moderately; small/regional lists are usually consulted by smaller mail servers only.' },
      { q: 'Can I check a /24?', a: 'Not via this tool — DNSBL lookups are per-IP. The Blacklist Monitoring add-on inside SieveGuard supports CIDR-range monitoring for tenants who manage IP allocations.' },
    ],
    relatedTools: [
      { slug: 'domain-checker', title: 'Domain health checker', tagline: 'Composite SPF / DKIM / DMARC / MX score for any domain.' },
      { slug: 'smtp-test', title: 'SMTP test tool', tagline: 'Probe an SMTP server live — EHLO, STARTTLS, MAIL FROM.' },
      { slug: 'email-spam-checker', title: 'Email spam checker', tagline: 'Score the content of your email against 15+ deliverability rules.' },
    ],
    ctaTitle: 'Monitor 90+ blacklists continuously',
    ctaBody:
      "SieveGuard's Blacklist Monitoring add-on runs this check daily on every sending IP and domain you onboard, against 90+ lists, with alerts the moment a new listing appears. Catch drift in hours, not weeks.",
  };

  return <ToolPageLayout content={content} />;
}

// Default export required by React Router framework-mode route modules.
export default BlacklistCheckerPage;
